import React, { useState, useEffect, useCallback } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faMagic, 
  faSave, 
  faUndo, 
  faEye, 
  faEdit, 
  faColumns, 
  faCheck, 
  faSync,
  faSpinner
} from '@fortawesome/free-solid-svg-icons';

import Button, { EButtonColor, EButtonSize } from '../../UI/Button/Button';
import Spinner from '../../UI/Spinner/Spinner';
import Alert, { EAlertColor } from '../../UI/Alert/Alert';
import { IOfferRequest, IOffer, IAppState, IOrganizationState, IAuthState } from '../../../interfaces';
import OfferService from '../../../services/OfferService';
import * as actions from '../../../store/actions';
import classes from './AiOfferModal.module.scss';

interface IProps {
  offerRequest: IOfferRequest;
  initialOffer?: Partial<IOffer> | null;
  onClose: () => void;
  onSaved?: (offer: IOffer) => void;
  onApplyToOffer?: (data: {
    message: string;
    priceWithVat?: number;
    priceVatP?: number;
    priceWithoutVat?: number;
  }) => void;
}

const AiOfferModal: React.FC<IProps> = ({
  offerRequest,
  initialOffer,
  onClose,
  onSaved,
  onApplyToOffer
}) => {
  const dispatch = useDispatch();

  const { authUser } = useSelector<IAppState, IAuthState>((state) => state.auth);
  const { organization } = useSelector<IAppState, IOrganizationState>((state) => state.organization);

  const [existingOffer, setExistingOffer] = useState<Partial<IOffer> | null>(initialOffer || null);
  const [loadingOffer, setLoadingOffer] = useState<boolean>(!initialOffer && !!offerRequest.id);

  // View mode: 'split' | 'edit' | 'preview'
  const [viewMode, setViewMode] = useState<'split' | 'edit' | 'preview'>('split');

  // Service pre-prompt states
  const [servicePrompt, setServicePrompt] = useState<string>('');
  const [defaultPrompt, setDefaultPrompt] = useState<string>('');
  const [isCustomPrompt, setIsCustomPrompt] = useState<boolean>(false);
  const [savingPrompt, setSavingPrompt] = useState<boolean>(false);
  const [promptSavedMsg, setPromptSavedMsg] = useState<string | null>(null);

  // Price states
  const [priceWithVat, setPriceWithVat] = useState<string>('');
  const [priceVatP, setPriceVatP] = useState<string>('25.5');
  const [priceWithoutVat, setPriceWithoutVat] = useState<string>('');

  // AI draft & message states
  const [message, setMessage] = useState<string>('');
  const [previousMessage, setPreviousMessage] = useState<string | null>(null);
  const [generatingAi, setGeneratingAi] = useState<boolean>(false);
  const [generateError, setGenerateError] = useState<string | null>(null);

  // Offer save state
  const [savingOffer, setSavingOffer] = useState<boolean>(false);
  const [offerError, setOfferError] = useState<string | null>(null);
  const [offerSuccessMsg, setOfferSuccessMsg] = useState<string | null>(null);

  const serviceToken = offerRequest.service?.serviceToken;

  // Load organization if not present
  useEffect(() => {
    if (authUser?.organization?.id && !organization) {
      dispatch(actions.getMyOrganization());
    }
  }, [dispatch, authUser, organization]);

  // Load initial prompt for this service
  useEffect(() => {
    const fallbackMasterPrompt = offerRequest.service?.aiPrompt || '';
    setDefaultPrompt(fallbackMasterPrompt);

    const targetOrg = organization || authUser?.organization;
    let savedCustomPrompt: string | null = null;

    if (targetOrg && targetOrg.aiServicePrompts && serviceToken) {
      try {
        const parsed = typeof targetOrg.aiServicePrompts === 'string'
          ? JSON.parse(targetOrg.aiServicePrompts)
          : targetOrg.aiServicePrompts;
        if (parsed && parsed[serviceToken]) {
          savedCustomPrompt = parsed[serviceToken];
        }
      } catch (e) {
        console.error('Error parsing aiServicePrompts in AiOfferModal:', e);
      }
    }

    if (savedCustomPrompt != null && savedCustomPrompt.trim() !== '') {
      setServicePrompt(savedCustomPrompt);
      setIsCustomPrompt(true);
    } else {
      setServicePrompt(fallbackMasterPrompt);
      setIsCustomPrompt(false);
    }
  }, [organization, authUser, offerRequest.service, serviceToken]);

  // Load offer if not passed as prop
  useEffect(() => {
    if (initialOffer) {
      setExistingOffer(initialOffer);
      if (initialOffer.priceWithVat != null) setPriceWithVat(String(initialOffer.priceWithVat));
      if (initialOffer.priceVatP != null) setPriceVatP(String(initialOffer.priceVatP));
      if (initialOffer.priceWithoutVat != null) setPriceWithoutVat(String(initialOffer.priceWithoutVat));
      if (initialOffer.message) setMessage(initialOffer.message);
      setLoadingOffer(false);
      return;
    }

    let isMounted = true;
    const fetchOffers = async () => {
      try {
        setLoadingOffer(true);
        const offers = await OfferService.getOffers(offerRequest.id);
        if (!isMounted) return;
        if (offers && offers.length > 0) {
          const current = offers[0];
          setExistingOffer(current);
          if (current.priceWithVat != null) setPriceWithVat(String(current.priceWithVat));
          if (current.priceVatP != null) setPriceVatP(String(current.priceVatP));
          if (current.priceWithoutVat != null) setPriceWithoutVat(String(current.priceWithoutVat));
          if (current.message) setMessage(current.message);
        }
      } catch (err) {
        console.error('Failed to load offer in AiOfferModal:', err);
      } finally {
        if (isMounted) setLoadingOffer(false);
      }
    };

    fetchOffers();
    return () => {
      isMounted = false;
    };
  }, [offerRequest.id, initialOffer]);

  // VAT calculations
  const calculateWithoutVat = useCallback((pWithVatVal: string, vatPVal: string) => {
    const pw = parseFloat(pWithVatVal.replace(',', '.')) || 0;
    const vp = parseFloat(vatPVal.replace(',', '.')) || 25.5;
    if (pw > 0) {
      const pwo = pw / (1 + vp / 100);
      return pwo.toFixed(2);
    }
    return '';
  }, []);

  const handlePriceWithVatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setPriceWithVat(val);
    setPriceWithoutVat(calculateWithoutVat(val, priceVatP));
  };

  const handlePriceVatPChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setPriceVatP(val);
    setPriceWithoutVat(calculateWithoutVat(priceWithVat, val));
  };

  const handlePriceWithoutVatChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value;
    setPriceWithoutVat(val);
    const pwo = parseFloat(val.replace(',', '.')) || 0;
    const vp = parseFloat(priceVatP.replace(',', '.')) || 25.5;
    if (pwo > 0) {
      const pw = pwo * (1 + vp / 100);
      setPriceWithVat(pw.toFixed(2));
    } else {
      setPriceWithVat('');
    }
  };

  // Generate / Test AI Draft
  const handleGenerateAi = async () => {
    try {
      setGeneratingAi(true);
      setGenerateError(null);
      setPromptSavedMsg(null);

      const pw = parseFloat(priceWithVat.replace(',', '.')) || 0;
      const vp = parseFloat(priceVatP.replace(',', '.')) || 25.5;
      const pwo = parseFloat(priceWithoutVat.replace(',', '.')) || (pw > 0 ? pw / (1 + vp / 100) : 0);

      const offerData: Partial<IOffer> = {
        priceWithVat: pw > 0 ? pw : undefined,
        priceWithoutVat: pwo > 0 ? Number(pwo.toFixed(2)) : undefined,
        priceVatP: vp,
        message: message || undefined
      };

      const draft = await OfferService.generateAiDraft(offerRequest.id, offerData, servicePrompt);
      if (message && message.trim() !== '') {
        setPreviousMessage(message);
      }
      setMessage(draft);
    } catch (err: any) {
      console.error('AI draft generation error:', err);
      setGenerateError(
        err?.response?.data?.message ||
        'AI-tarjousvastauksen generointi epäonnistui. Tarkista verkkoyhteys ja kokeile uudelleen.'
      );
    } finally {
      setGeneratingAi(false);
    }
  };

  const handleUndoMessage = () => {
    if (previousMessage != null) {
      const current = message;
      setMessage(previousMessage);
      setPreviousMessage(current);
    }
  };

  // Save prompt to company's permanent config
  const handleSavePrompt = async () => {
    const targetOrg = organization || authUser?.organization;
    if (!targetOrg || !serviceToken) {
      setGenerateError('Yrityksen tai palvelun tunnistetta ei löytynyt tallennusta varten.');
      return;
    }

    try {
      setSavingPrompt(true);
      setPromptSavedMsg(null);

      let parsedPrompts: Record<string, string> = {};
      if (targetOrg.aiServicePrompts) {
        try {
          parsedPrompts = typeof targetOrg.aiServicePrompts === 'string'
            ? JSON.parse(targetOrg.aiServicePrompts)
            : { ...(targetOrg.aiServicePrompts as any) };
        } catch (e) {}
      }

      parsedPrompts[serviceToken] = servicePrompt;
      const updatedOrg = {
        ...targetOrg,
        aiServicePrompts: JSON.stringify(parsedPrompts)
      };

      await dispatch(actions.updateMyOrganization(updatedOrg) as any);
      setIsCustomPrompt(true);
      setPromptSavedMsg('Ohjeistus tallennettu onnistuneesti yrityksen pysyväksi oletukseksi!');
    } catch (err: any) {
      console.error('Failed to save service prompt:', err);
      setGenerateError('Ohjeistuksen tallennus epäonnistui.');
    } finally {
      setSavingPrompt(false);
    }
  };

  const handleResetPrompt = () => {
    setServicePrompt(defaultPrompt);
    setIsCustomPrompt(false);
    setPromptSavedMsg('Palautettu palvelun yleinen oletusohje.');
  };

  // Save offer to backend
  const handleSaveOffer = async (e?: React.MouseEvent) => {
    if (e) e.preventDefault();
    try {
      setSavingOffer(true);
      setOfferError(null);
      setOfferSuccessMsg(null);

      const pw = parseFloat(priceWithVat.replace(',', '.')) || 0;
      const vp = parseFloat(priceVatP.replace(',', '.')) || 25.5;
      const pwo = pw > 0 ? Number((pw / (1 + vp / 100)).toFixed(2)) : 0;
      const pv = pw > 0 ? Number((pw - pwo).toFixed(2)) : 0;

      const currentState = existingOffer?.state || 'DRAFT';
      const nextState = (currentState === 'DRAFT' || !currentState) && pw > 0 ? 'SENT' : currentState;

      const offerPayload: Partial<IOffer> = {
        offerRequestId: offerRequest.id,
        state: nextState,
        priceWithVat: pw > 0 ? pw : undefined,
        priceWithoutVat: pwo > 0 ? pwo : undefined,
        priceVat: pv > 0 ? pv : undefined,
        priceVatP: vp,
        message: message,
        archived: existingOffer?.archived || false
      };

      let saved: IOffer;
      if (existingOffer?.id) {
        saved = await OfferService.updateOffer(existingOffer.id, offerPayload);
      } else {
        saved = await OfferService.saveOffer(offerPayload as IOffer);
      }

      setExistingOffer(saved);
      setOfferSuccessMsg('Tarjous tallennettu onnistuneesti!');
      if (onSaved) onSaved(saved);
      if (onApplyToOffer) {
        onApplyToOffer({
          message,
          priceWithVat: pw > 0 ? pw : undefined,
          priceVatP: vp,
          priceWithoutVat: pwo > 0 ? pwo : undefined
        });
      }
    } catch (err: any) {
      console.error('Failed to save offer:', err);
      setOfferError(err?.response?.data?.message || 'Tarjouksen tallennus epäonnistui.');
    } finally {
      setSavingOffer(false);
    }
  };

  const handleApplyToOffer = () => {
    const pw = parseFloat(priceWithVat.replace(',', '.')) || 0;
    const vp = parseFloat(priceVatP.replace(',', '.')) || 25.5;
    const pwo = pw > 0 ? Number((pw / (1 + vp / 100)).toFixed(2)) : 0;

    if (onApplyToOffer) {
      onApplyToOffer({
        message,
        priceWithVat: pw > 0 ? pw : undefined,
        priceVatP: vp,
        priceWithoutVat: pwo > 0 ? pwo : undefined
      });
    }
    onClose();
  };

  // Preview company data
  const targetOrg = organization || authUser?.organization;
  const companyName = targetOrg?.aiCompanyName || targetOrg?.name || 'HankiX Palveluntarjoaja';
  const contactName = targetOrg?.aiContactName || authUser?.name || '';
  const email = targetOrg?.aiEmail || authUser?.email || '';
  const phone = targetOrg?.aiPhone || targetOrg?.phone || '';
  const footerText = targetOrg?.aiFooter || '';

  const parsedPriceWithVat = parseFloat(priceWithVat.replace(',', '.')) || 0;
  const parsedPriceWithoutVat = parseFloat(priceWithoutVat.replace(',', '.')) || 0;
  const vatAmount = parsedPriceWithVat > 0 && parsedPriceWithoutVat > 0 
    ? (parsedPriceWithVat - parsedPriceWithoutVat).toFixed(2) 
    : '';

  // Render Customer Preview Component
  const renderCustomerPreview = () => (
    <div className={classes.CustomerPreviewBox}>
      <div className={classes.PreviewNotice}>
        <FontAwesomeIcon icon={faEye} />
        <span>Tarjousviestin esikatselu</span>
      </div>

      <div className={classes.PreviewHeader}>
        <div className={classes.PreviewCompany}>
          <h3>{companyName}</h3>
          {(contactName || email || phone) && (
            <p>
              {[contactName, phone, email].filter(Boolean).join(' · ')}
            </p>
          )}
        </div>
        <div className={classes.PreviewService}>
          <div className={classes.Title}>{offerRequest.service?.name || 'Palvelutarjous'}</div>
          <div className={classes.Date}>Pyyntö luotu {offerRequest.created?.substring(0, 10)}</div>
        </div>
      </div>

      <div className={classes.PreviewBody}>
        <div className={classes.PreviewPropertyCard}>
          <div style={{ fontWeight: 600, color: '#1e293b' }}>
            Kohde ja tilaaja:
          </div>
          <div>
            {[offerRequest.firstName, offerRequest.lastName].filter(Boolean).join(' ')}
          </div>
          <div>
            {[offerRequest.address, offerRequest.zip, offerRequest.city].filter(Boolean).join(', ')}
          </div>
        </div>

        {parsedPriceWithVat > 0 && (
          <div className={classes.PreviewPriceBox}>
            <div>
              <div className={classes.PriceSub}>Tarjouksen kokonaishinta</div>
              <div className={classes.PriceMain}>
                {parsedPriceWithVat.toFixed(2).replace('.', ',')} €
              </div>
            </div>
            <div style={{ textAlign: 'right' }}>
              <div className={classes.PriceSub}>
                Sisältää alv {priceVatP}% ({vatAmount.replace('.', ',')} €)
              </div>
              <div style={{ fontSize: '0.85rem', color: '#166534' }}>
                Veroton hinta: {parsedPriceWithoutVat.toFixed(2).replace('.', ',')} €
              </div>
            </div>
          </div>
        )}

        <div className={classes.PreviewMessageContent}>
          {message && message.trim() !== '' ? (
            message
          ) : (
            <span style={{ color: '#94a3b8', fontStyle: 'italic' }}>
              Tarjousviestiä ei ole vielä luotu. Muokkaa pre-promptia tai syötä hinta ja paina "Generoi AI-vastaus / Testaa".
            </span>
          )}
        </div>

        {footerText && (
          <div className={classes.PreviewFooterSignature}>
            {footerText}
          </div>
        )}
      </div>
    </div>
  );

  if (loadingOffer) {
    return (
      <div style={{ padding: '3rem', textAlign: 'center' }}>
        <Spinner />
        <p style={{ marginTop: '1rem', color: '#64748b' }}>Ladataan tarjoustietoja...</p>
      </div>
    );
  }

  return (
    <div className={classes.ModalWrapper}>
      {/* Header Summary */}
      <div className={classes.HeaderSummary}>
        <div className={classes.ServiceTag}>
          <span 
            className={classes.ServiceBadge}
            style={offerRequest.service?.brandColor ? { backgroundColor: offerRequest.service.brandColor } : {}}
          >
            {offerRequest.service?.name || 'Palvelu'}
          </span>
          <div className={classes.CustomerSummary}>
            <span>
              <strong>Tilaaja:</strong> {[offerRequest.firstName, offerRequest.lastName].filter(Boolean).join(' ') || 'Ei nimeä'}
            </span>
            <span>·</span>
            <span>
              <strong>Sijainti:</strong> {[offerRequest.address, offerRequest.city].filter(Boolean).join(', ')}
            </span>
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
          <div className={classes.StatusBadge}>
            Tila: {existingOffer?.state === 'SENT' ? 'Lähetetty' : existingOffer?.state === 'WON' ? 'Voitettu' : existingOffer?.state === 'LOST' ? 'Ei tilannut' : 'Luonnos'}
          </div>

          <div className={classes.ViewModeToggle}>
            <button
              type="button"
              className={`${classes.ViewTab} ${viewMode === 'split' ? classes.ActiveTab : ''}`}
              onClick={() => setViewMode('split')}
              title="Jaettu näkymä rinnakkain"
            >
              <FontAwesomeIcon icon={faColumns} />
              <span>Rinnakkain</span>
            </button>
            <button
              type="button"
              className={`${classes.ViewTab} ${viewMode === 'edit' ? classes.ActiveTab : ''}`}
              onClick={() => setViewMode('edit')}
              title="Vain muokkaus & prompt"
            >
              <FontAwesomeIcon icon={faEdit} />
              <span>Muokkaus</span>
            </button>
            <button
              type="button"
              className={`${classes.ViewTab} ${viewMode === 'preview' ? classes.ActiveTab : ''}`}
              onClick={() => setViewMode('preview')}
              title="Asiakasnäkymän esikatselu"
            >
              <FontAwesomeIcon icon={faEye} />
              <span>Esikatselu</span>
            </button>
          </div>
        </div>
      </div>

      {/* Notifications */}
      {promptSavedMsg && (
        <Alert color={EAlertColor.SUCCESS}>
          {promptSavedMsg}
        </Alert>
      )}
      {offerSuccessMsg && (
        <Alert color={EAlertColor.SUCCESS}>
          {offerSuccessMsg}
        </Alert>
      )}
      {(generateError || offerError) && (
        <Alert color={EAlertColor.DANGER}>
          {generateError || offerError}
        </Alert>
      )}

      {/* Main Layout */}
      <div className={`${classes.MainLayout} ${viewMode === 'split' ? classes.Split : classes.SingleColumn}`}>
        {/* Left Column: Edit & Test Prompt */}
        {(viewMode === 'split' || viewMode === 'edit') && (
          <div className={classes.LeftColumn}>
            {/* Pre-prompt Box */}
            <div className={classes.Card}>
              <div className={classes.CardHeader}>
                <div className={classes.CardTitle}>
                  <FontAwesomeIcon icon={faMagic} style={{ color: '#e2512c' }} />
                  <span>AI-ohjeistus tälle palvelulle (Pre-prompt)</span>
                </div>
                <span className={`${classes.PromptBadge} ${isCustomPrompt ? classes.Custom : classes.Default}`}>
                  {isCustomPrompt ? 'Yrityksen oma ohje' : 'Palvelun oletus'}
                </span>
              </div>

              <textarea
                className={classes.PromptTextarea}
                value={servicePrompt}
                onChange={(e) => setServicePrompt(e.target.value)}
                placeholder="Esim. 'Korosta vastauksessa nopeaa toimitusaikaa (3 arkipäivää) ja avaimet käteen -toteutusta...'"
                rows={4}
              />

              <div className={classes.PromptActions}>
                <div className={classes.ButtonGroup}>
                  <Button
                    onClick={handleGenerateAi}
                    disabled={generatingAi}
                    size={EButtonSize.SMALL}
                    color={EButtonColor.PRIMARY}
                  >
                    {generatingAi ? (
                      <>
                        <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '6px' }} />
                        <span>Generoidaan...</span>
                      </>
                    ) : (
                      <>
                        <FontAwesomeIcon icon={faMagic} style={{ marginRight: '6px' }} />
                        <span>Generoi AI-vastaus / Testaa</span>
                      </>
                    )}
                  </Button>

                  {previousMessage && (
                    <Button
                      onClick={handleUndoMessage}
                      size={EButtonSize.SMALL}
                      color={EButtonColor.SECONDARY}
                      title="Palauta edellinen vastausversio"
                    >
                      <FontAwesomeIcon icon={faUndo} style={{ marginRight: '4px' }} />
                      <span>Kumoa</span>
                    </Button>
                  )}
                </div>

                <div className={classes.ButtonGroup}>
                  <Button
                    onClick={handleSavePrompt}
                    disabled={savingPrompt || !servicePrompt}
                    size={EButtonSize.SMALL}
                    color={EButtonColor.SECONDARY}
                    title="Tallentaa tämän ohjeen yrityksesi pysyväksi palveluohjeeksi"
                  >
                    <FontAwesomeIcon icon={faSave} style={{ marginRight: '4px' }} />
                    <span>{savingPrompt ? 'Tallennetaan...' : 'Tallenna yrityksen ohjeeksi'}</span>
                  </Button>

                  {defaultPrompt && (
                    <Button
                      onClick={handleResetPrompt}
                      size={EButtonSize.SMALL}
                      color={EButtonColor.SECONDARY}
                      title="Palauta palvelun järjestelmäoletus"
                    >
                      <FontAwesomeIcon icon={faSync} style={{ marginRight: '4px' }} />
                      <span>Palauta oletus</span>
                    </Button>
                  )}
                </div>
              </div>
            </div>

            {/* Price Inputs */}
            <div className={classes.Card}>
              <div className={classes.CardHeader}>
                <div className={classes.CardTitle}>
                  Tarjouksen hinta
                </div>
              </div>

              <div className={classes.PriceSection}>
                <div className={classes.InputWrapper}>
                  <label>Kokonaishinta (€ sis. alv)</label>
                  <input
                    type="text"
                    value={priceWithVat}
                    onChange={handlePriceWithVatChange}
                    placeholder="Esim. 350"
                  />
                </div>

                <div className={classes.InputWrapper}>
                  <label>Alv (%)</label>
                  <input
                    type="text"
                    value={priceVatP}
                    onChange={handlePriceVatPChange}
                    placeholder="25.5"
                  />
                </div>

                <div className={classes.InputWrapper}>
                  <label>Veroton hinta (€)</label>
                  <input
                    type="text"
                    value={priceWithoutVat}
                    onChange={handlePriceWithoutVatChange}
                    placeholder="Esim. 278.88"
                  />
                </div>
              </div>
            </div>

            {/* Message Textarea */}
            <div className={classes.Card}>
              <div className={classes.CardHeader}>
                <div className={classes.CardTitle}>
                  Viesti asiakkaalle
                </div>
              </div>

              <textarea
                className={classes.MessageTextarea}
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                placeholder="Tekoälyn generoima tarjousviesti tai oma muokattu viestisi asiakkaalle..."
                rows={8}
              />

              <div className={classes.MessageMeta}>
                <span>Merkkejä: {message.length}</span>
                <span>Sanoja: {message.trim() ? message.trim().split(/\s+/).length : 0}</span>
              </div>
            </div>
          </div>
        )}

        {/* Right Column: Customer Preview */}
        {(viewMode === 'split' || viewMode === 'preview') && (
          <div className={classes.RightColumn}>
            {renderCustomerPreview()}
          </div>
        )}
      </div>

      {/* Footer Bar */}
      <div className={classes.ModalFooterBar}>
        <div style={{ display: 'flex', gap: '8px' }}>
          <Button
            onClick={onClose}
            size={EButtonSize.MEDIUM}
            color={EButtonColor.SECONDARY}
          >
            Sulje
          </Button>
        </div>

        <div style={{ display: 'flex', gap: '10px' }}>
          {onApplyToOffer && (
            <Button
              onClick={handleApplyToOffer}
              size={EButtonSize.MEDIUM}
              color={EButtonColor.SECONDARY}
            >
              <FontAwesomeIcon icon={faCheck} style={{ marginRight: '6px' }} />
              Käytä tarjouksessa
            </Button>
          )}

          <Button
            onClick={handleSaveOffer}
            disabled={savingOffer}
            size={EButtonSize.MEDIUM}
            color={EButtonColor.PRIMARY}
          >
            {savingOffer ? (
              <>
                <FontAwesomeIcon icon={faSpinner} spin style={{ marginRight: '6px' }} />
                Tallennetaan...
              </>
            ) : (
              <>
                <FontAwesomeIcon icon={faSave} style={{ marginRight: '6px' }} />
                Tallenna tarjous
              </>
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};

export default AiOfferModal;
